/**
 * Centralized error handling utilities for CollabCanvas
 * Provides consistent error messages, retry logic, and offline support
 */

// Firebase error code mappings to user-friendly messages
const FIREBASE_ERROR_MESSAGES = {
  // Auth errors
  'auth/user-not-found': 'No account found with this email address.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/weak-password': 'Password should be at least 6 characters.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/popup-blocked': 'Popup blocked. Please allow popups for this site.',
  'auth/cancelled-popup-request': 'Sign-in was cancelled.',
  'auth/popup-closed-by-user': 'Sign-in window was closed.',
  
  // Firestore errors
  'firestore/permission-denied': 'You don\'t have permission to perform this action.',
  'firestore/not-found': 'The requested data could not be found.',
  'firestore/already-exists': 'This item already exists.',
  'firestore/resource-exhausted': 'Too many requests. Please slow down.',
  'firestore/failed-precondition': 'The operation failed due to a conflict.',
  'firestore/aborted': 'The operation was cancelled due to a conflict.',
  'firestore/out-of-range': 'The request is outside the valid range.',
  'firestore/unimplemented': 'This feature is not available.',
  'firestore/internal': 'Internal server error. Please try again.',
  'firestore/unavailable': 'Service temporarily unavailable.',
  'firestore/data-loss': 'Data may have been lost. Please refresh.',
  
  // Network errors
  'network-request-failed': 'Network connection failed. Please check your internet.',
  'offline': 'You appear to be offline. Changes will be saved when you reconnect.',
  'timeout': 'Request timed out. Please try again.',
  
  // Generic fallbacks
  'unknown': 'An unexpected error occurred. Please try again.',
  'default': 'Something went wrong. Please try again later.'
};

// Error types for consistent handling
export const ERROR_TYPES = {
  NETWORK: 'network',
  AUTH: 'auth', 
  FIRESTORE: 'firestore',
  VALIDATION: 'validation',
  PERMISSION: 'permission',
  UNKNOWN: 'unknown'
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'low',       // Minor issues, user can continue
  MEDIUM: 'medium', // Important issues, some functionality affected
  HIGH: 'high',     // Critical issues, major functionality broken
  CRITICAL: 'critical' // App-breaking issues, user should refresh
};

/**
 * Create standardized error object
 * @param {Error|string} error - Original error or error message
 * @param {string} type - Error type from ERROR_TYPES
 * @param {string} severity - Error severity from ERROR_SEVERITY
 * @param {Object} metadata - Additional error context
 * @returns {Object} Standardized error object
 */
export function createError(error, type = ERROR_TYPES.UNKNOWN, severity = ERROR_SEVERITY.MEDIUM, metadata = {}) {
  const errorCode = error?.code || error?.message || 'unknown';
  const userMessage = getUserFriendlyMessage(errorCode);
  
  return {
    code: errorCode,
    message: error?.message || error,
    userMessage,
    type,
    severity,
    timestamp: new Date().toISOString(),
    metadata: {
      ...metadata,
      originalError: error
    }
  };
}

/**
 * Get user-friendly error message based on error code
 * @param {string} errorCode - Firebase error code or custom code
 * @returns {string} User-friendly error message
 */
export function getUserFriendlyMessage(errorCode) {
  // Remove 'auth/' or 'firestore/' prefix for lookup
  const cleanCode = errorCode.replace(/^(auth|firestore)\//, '');
  
  return FIREBASE_ERROR_MESSAGES[errorCode] || 
         FIREBASE_ERROR_MESSAGES[cleanCode] || 
         FIREBASE_ERROR_MESSAGES.default;
}

/**
 * Check if error is network-related
 * @param {Error|Object} error - Error to check
 * @returns {boolean} True if network error
 */
export function isNetworkError(error) {
  const code = error?.code || error?.message || '';
  const networkCodes = [
    'network-request-failed',
    'offline',
    'timeout', 
    'unavailable',
    'NETWORK_ERROR',
    'ERR_NETWORK',
    'ERR_INTERNET_DISCONNECTED'
  ];
  
  return networkCodes.some(netCode => 
    code.toLowerCase().includes(netCode.toLowerCase())
  );
}

/**
 * Check if error is retryable
 * @param {Error|Object} error - Error to check  
 * @returns {boolean} True if error can be retried
 */
export function isRetryableError(error) {
  const code = error?.code || error?.message || '';
  const retryableCodes = [
    'network-request-failed',
    'unavailable',
    'timeout',
    'internal',
    'aborted',
    'resource-exhausted'
  ];
  
  return retryableCodes.some(retryCode => 
    code.toLowerCase().includes(retryCode.toLowerCase())
  );
}

/**
 * Retry function with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise} Result of successful function call
 */
export async function retryWithBackoff(fn, maxRetries = 3, baseDelay = 1000) {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries || !isRetryableError(error)) {
        throw createError(
          error, 
          isNetworkError(error) ? ERROR_TYPES.NETWORK : ERROR_TYPES.UNKNOWN,
          ERROR_SEVERITY.HIGH,
          { attempts: attempt, maxRetries }
        );
      }
      
      // Exponential backoff: 1s, 2s, 4s, etc.
      const delay = baseDelay * Math.pow(2, attempt - 1);
      console.warn(`Attempt ${attempt} failed, retrying in ${delay}ms...`, error);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

/**
 * Network status manager
 */
export class NetworkStatusManager {
  constructor() {
    this.isOnline = navigator.onLine;
    this.listeners = new Set();
    this.operationQueue = [];
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
  }
  
  /**
   * Add listener for network status changes
   * @param {Function} callback - Function to call on status change
   */
  addListener(callback) {
    this.listeners.add(callback);
  }
  
  /**
   * Remove network status listener
   * @param {Function} callback - Function to remove
   */
  removeListener(callback) {
    this.listeners.delete(callback);
  }
  
  /**
   * Handle coming back online
   */
  handleOnline() {
    this.isOnline = true;
    console.log('🌐 Connection restored, processing queued operations...');
    
    // Notify listeners
    this.listeners.forEach(callback => {
      try {
        callback({ isOnline: true, operationQueue: [...this.operationQueue] });
      } catch (error) {
        console.error('Error in network status listener:', error);
      }
    });
    
    // Process queued operations
    this.processQueuedOperations();
  }
  
  /**
   * Handle going offline
   */
  handleOffline() {
    this.isOnline = false;
    console.log('📡 Connection lost, operations will be queued...');
    
    // Notify listeners
    this.listeners.forEach(callback => {
      try {
        callback({ isOnline: false });
      } catch (error) {
        console.error('Error in network status listener:', error);
      }
    });
  }
  
  /**
   * Queue operation for later execution
   * @param {Function} operation - Operation to queue
   * @param {string} description - Human-readable description
   */
  queueOperation(operation, description = 'Unknown operation') {
    this.operationQueue.push({
      operation,
      description,
      timestamp: Date.now(),
      id: Math.random().toString(36).substr(2, 9)
    });
    
    console.log(`📝 Queued operation: ${description}`);
  }
  
  /**
   * Process all queued operations
   */
  async processQueuedOperations() {
    if (!this.isOnline || this.operationQueue.length === 0) {
      return;
    }
    
    const operations = [...this.operationQueue];
    this.operationQueue = [];
    
    console.log(`🔄 Processing ${operations.length} queued operations...`);
    
    for (const { operation, description, id } of operations) {
      try {
        await operation();
        console.log(`✅ Completed queued operation: ${description}`);
      } catch (error) {
        console.error(`❌ Failed queued operation: ${description}`, error);
        
        // Re-queue if it's a retryable error
        if (isRetryableError(error)) {
          this.operationQueue.push({ operation, description, id, timestamp: Date.now() });
        }
      }
    }
  }
  
  /**
   * Clear all queued operations
   */
  clearQueue() {
    this.operationQueue = [];
  }
}

// Global network status manager instance
export const networkManager = new NetworkStatusManager();

/**
 * Wrap Firebase operation with error handling and retry logic
 * @param {Function} operation - Firebase operation to wrap
 * @param {string} operationName - Name for logging/queuing
 * @param {Object} options - Configuration options
 * @returns {Promise} Result of operation or error
 */
export async function withErrorHandling(operation, operationName = 'Firebase operation', options = {}) {
  const {
    enableRetry = true,
    enableOfflineQueue = true,
    maxRetries = 3,
    errorType = ERROR_TYPES.UNKNOWN
  } = options;
  
  try {
    // If offline and queuing is enabled, queue the operation
    if (!networkManager.isOnline && enableOfflineQueue) {
      networkManager.queueOperation(operation, operationName);
      throw createError(
        'offline',
        ERROR_TYPES.NETWORK,
        ERROR_SEVERITY.MEDIUM,
        { queued: true, operationName }
      );
    }
    
    // Execute with retry if enabled
    if (enableRetry) {
      return await retryWithBackoff(operation, maxRetries);
    } else {
      return await operation();
    }
    
  } catch (error) {
    // Create standardized error
    const standardError = createError(
      error,
      errorType,
      isNetworkError(error) ? ERROR_SEVERITY.HIGH : ERROR_SEVERITY.MEDIUM,
      { operationName }
    );
    
    console.error(`${operationName} failed:`, standardError);
    throw standardError;
  }
}

/**
 * Log error with context for debugging
 * @param {Object} error - Standardized error object
 * @param {string} context - Additional context
 */
export function logError(error, context = '') {
  console.group(`❌ Error: ${error.userMessage}`);
  console.error('Code:', error.code);
  console.error('Type:', error.type);
  console.error('Severity:', error.severity);
  console.error('Timestamp:', error.timestamp);
  if (context) console.error('Context:', context);
  if (error.metadata) console.error('Metadata:', error.metadata);
  console.groupEnd();
}

export default {
  createError,
  getUserFriendlyMessage,
  isNetworkError,
  isRetryableError,
  retryWithBackoff,
  withErrorHandling,
  networkManager,
  logError,
  ERROR_TYPES,
  ERROR_SEVERITY
};
